/**
 * Shared formatters for handoff hotness score and signal/reason labels, so the
 * queue card, the pending-handoff notification, and the queue modal all render
 * them the same way.
 */

/** Round the hotness score for display (backend stores a float). */
export const formatHotnessScore = (score: number | null | undefined): number =>
  Math.round(score ?? 0);

// Friendly labels for the known hotness signals; anything else falls back to a
// humanized split of the raw key.
//
// SPEC-2296: Sara asked for specific agent-facing wording so the labels read
// like what an agent expects, not like classifier field names — "callback" →
// "call back request", "agentRequest" → "speak with agent", "tour" → "showing
// request". Rendered lowercase to match the tag style of the chips, which is
// also what the humanized fallback below produces for unknown keys. One map, so
// every render path (queue card, pending-handoff notification, landing pages)
// picks up a change here.
const SIGNAL_LABELS: Record<string, string> = {
  buyingIntent: 'buying intent',
  sellingIntent: 'selling intent',
  urgency: 'urgent',
  tour: 'showing request',
  callback: 'callback request',
  agentRequest: 'speak with agent',
  preApproved: 'pre-approved',
  timeline: 'timeline',
  appointmentRequest: 'appointment request',
  textPreference: 'text preference',
  renter: 'renter',
  languagePreference: 'language preference',
  liveTransferCompleted: 'live transfer connected',
  liveTransferMissed: 'live transfer missed',
};

/** Lowercase, tag-style label: "buyingIntent" / "buying_intent" -> "buying intent".
 *  The backend emits keys as camelCase (legacy) or snake_case (SPEC-1919 rich
 *  signals), so we look up both forms before falling back to a humanized split. */
export const formatSignalLabel = (name: string): string => {
  const raw = name.trim();
  const camel = raw.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
  if (SIGNAL_LABELS[raw]) return SIGNAL_LABELS[raw];
  if (SIGNAL_LABELS[camel]) return SIGNAL_LABELS[camel];
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

/** A handoff reason is either a legacy string ("Signal: buyingIntent" or a plain
 *  sentence) or the SPEC-1919 rich shape `{ key, note }` (key = signal name,
 *  note = the evidence sentence). Kept as `unknown`-tolerant so a shape drift
 *  never renders an object straight into JSX (React error #31). */
type RichReason = { key?: string; note?: string };
const asRich = (reason: unknown): RichReason | null =>
  reason && typeof reason === 'object' ? (reason as RichReason) : null;

/** Human-readable text for a single reason, whatever shape it arrives in. */
export const reasonText = (reason: unknown): string => {
  if (typeof reason === 'string') return reason;
  const rich = asRich(reason);
  if (rich?.note) return rich.note;
  if (rich?.key) return formatSignalLabel(rich.key);
  return '';
};

/**
 * Turn a reason into a clean signal label: legacy `"Signal: X"` strings and the
 * rich `{ key }` shape both resolve to the signal key's label; anything else
 * passes through as its own text.
 */
export const formatHandoffReason = (reason: unknown): string => {
  if (typeof reason === 'string') {
    const match = reason.match(/^\s*signal:\s*(.+)$/i);
    return match ? formatSignalLabel(match[1]) : reason;
  }
  return reasonText(reason);
};

/** The signal-flag labels from a handoff's `reasons` (drops non-signal reasons).
 *  Handles both the legacy `"Signal: X"` strings and the rich `{ key, note }`
 *  objects — each recognized signal becomes one chip label. */
export const signalFlagsFromReasons = (
  reasons: ReadonlyArray<unknown> | null | undefined
): string[] =>
  (reasons ?? [])
    .map((r): string | null => {
      if (typeof r === 'string') {
        const match = r.match(/^\s*signal:\s*(.+)$/i);
        return match ? formatSignalLabel(match[1]) : null;
      }
      const key = asRich(r)?.key;
      return key ? formatSignalLabel(key) : null;
    })
    .filter((label): label is string => !!label);

/**
 * The signal chips for a handoff.
 *
 * The authoritative source is the `signals` dict the backend writes on every row
 * — `{ buyingIntent: true, liveTransferMissed: true }` — where only keys set to
 * `true` count. `reasons` is folded in as well because older rows encoded
 * signals there instead, either as ``"Signal: X"`` strings or as SPEC-1919
 * `{ key, note }` objects.
 *
 * Reading `reasons` alone (which every surface did) meant a row whose reasons
 * were free text rendered no chips at all, even with a fully populated `signals`
 * dict sitting right next to it.
 */
export const signalFlagsFrom = (
  signals: Record<string, boolean | undefined> | null | undefined,
  reasons: ReadonlyArray<unknown> | null | undefined
): string[] => {
  const labels = new Set<string>();
  for (const [key, value] of Object.entries(signals ?? {})) {
    if (value === true) labels.add(formatSignalLabel(key));
  }
  for (const label of signalFlagsFromReasons(reasons)) {
    labels.add(label);
  }
  return Array.from(labels);
};
